// src/pages/AboutPage/AboutPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./aboutPage.css";
import { AboutContentStore } from "./AboutContentStore";


export default function AboutScreen({ store }) {
  const navigate = useNavigate();
  const contentStore = useMemo(
    () => store || new AboutContentStore({ source: "local" }),
    [store]
  );

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  // track current slide (for "active" dot + small heading updates if you want)
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setErr("");
        const res = await contentStore.getAboutPageContent();
        if (mounted) setData(res);
      } catch (e) {
        if (mounted) setErr(e?.message || "Failed to load About content");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [contentStore]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      // find nearest slide based on scrollLeft
      const children = Array.from(el.querySelectorAll("[data-slide='1']"));
      if (!children.length) return;

      const left = el.scrollLeft;
      let bestIdx = 0;
      let bestDist = Infinity;

      children.forEach((node, idx) => {
        const dist = Math.abs(node.offsetLeft - left);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });

      setActiveIndex(bestIdx);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [data]);

  const onAction = (id) =>{
    if(id=="a1"){
      navigate("/pricing")
    }else{
       navigate("/about", { replace: true });
    }
  }

  if (err) {
    return (
      <div className="paper-grid about-wrap">
        <div className="about-shell shadow-soft">
          <h1 className="about-h1">About</h1>
          <p className="about-sub">Could not load content.</p>
          <p className="about-error">{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="paper-grid about-wrap">
        <div className="about-shell shadow-soft">
          <div className="about-loading">Loading…</div>
        </div>
      </div>
    );
  }

  const slides = data.heroSlides || [];
  const activeSlide = slides[activeIndex] || slides[0];

  return (
    <div className="paper-grid about-wrap">
      <div className="about-shell">
        {/* HERO */}
        <section className="hero">
          <div className="hero-top">
            <div>
              <h1 className="about-h1">{data.pageTitle}</h1>
              <p className="about-sub">{data.pageSubtitle}</p>
            </div>

            {/* optional small badge for current slide */}
          </div>

          <div className="hero-rail-wrap">

            {/* Horizontal scroller */}
            <div className="hero-rail" ref={scrollerRef} aria-label="About highlights">
              {slides.map((s, idx) => (
                <article
                  key={s.id}
                  data-slide="1"
                  className="hero-card shadow-soft"
                >
                  <div className="hero-media">
                    {/* Image */}
                    <img className="hero-img" src={s.imageUrl} alt={s.alt || s.headline} loading="lazy" />
                    {/* Light overlay for readability */}
                    <div className="hero-overlay" />
                    {/* Text overlay */}
                    <div className="hero-text">
                      {s.kicker ? <div className="hero-kicker">{s.kicker}</div> : null}
                      <div className="hero-headline">{s.headline}</div>
                      {s.subtext ? <div className="hero-subtext">{s.subtext}</div> : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* BODY */}
        <section className="about-body">
          {/* What is Okul */}
          <div className="section-card shadow-soft">
            <div className="section-title">{data.whatIs.title}</div>
            <div className="section-lede">{data.whatIs.lede}</div>
            <div className="section-grid">
              {data.whatIs.paragraphs?.map((p, i) => (
                <p key={i} className="p">{p}</p>
              ))}
            </div>
          </div>

          {/* Beliefs */}
          <div className="section-card shadow-soft">
            <div className="section-title">{data.beliefs.title}</div>
            <div className="beliefs">
              {data.beliefs.items?.map((b) => (
                <div key={b.id} className="belief">
                  <div className="belief-ic" aria-hidden="true">
                    {b.icon}
                  </div>
                  <div className="belief-h">{b.heading}</div>
                  <div className="belief-t">{b.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div className="section-card shadow-soft">
            <div className="section-title">{data.howItWorks.title}</div>
            <div className="steps">
              {data.howItWorks.steps?.map((st, idx) => (
                <div key={st.id} className="step">
                  <div className="step-n">{String(idx + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="step-h">{st.heading}</div>
                    <div className="step-t">{st.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="section-card shadow-soft">
            <div className="section-title">{data.stats.title}</div>
            <div className="stats">
              {data.stats.items?.map((x) => (
                <div key={x.id} className="stat">
                  <div className="stat-v">{x.value}</div>
                  <div className="stat-k">{x.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Closing */}
          <div className="closing shadow-soft">
            <div className="closing-title">{data.closing.title}</div>
            <div className="closing-text">{data.closing.text}</div>
            <div className="closing-actions">
              {data.closing.actions?.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={["btn", a.variant === "primary" ? "btn-primary" : "btn-ghost"].join(" ")}
                  onClick={() => {
                    onAction(a.id)
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="about-foot">
          <span>{data.footerNote}</span>
        </div>
      </div>
    </div>
  );
}
